import type { AgentInfo, Model, ModelRef } from "./opencodeTypes"

// Slash commands for the chat composer.
//
// Kept out of the component and free of React so the parsing and the option
// building can be reasoned about, and tested, on their own — see
// __tests__/slashCommands.test.ts.

export interface SlashCommand {
  name: string
  description: string
  /**
   * Which picker the argument stage opens.
   *
   * Absent means the command takes no argument and runs the moment it is
   * chosen. This has to be declared rather than inferred: the menu used to
   * test `name === "model"` and treat *everything else* as /effort, which made
   * every other built-in unreachable — picking /compact silently changed the
   * reasoning depth instead. A command that grows a picker now says so here,
   * and the one place that switches on it (MessageInput) cannot guess wrong.
   */
  picker?: "model" | "effort" | "agent"
  /** True for commands that come from the agent's config (opencode.json). */
  isCustom?: boolean
}

// Built-in commands handled client-side. Custom commands from the agent are
// merged in at display time — they share the same name/description shape but
// are dispatched server-side via POST /session/:id/command.
export const BUILT_IN_COMMANDS: SlashCommand[] = [
  { name: "model", description: "Choose the model this session talks to", picker: "model" },
  { name: "effort", description: "Choose how much reasoning the model spends", picker: "effort" },
  { name: "agent", description: "Choose the agent this session runs as", picker: "agent" },
  // The two primary agents every container ships, as direct shortcuts. They are
  // named here rather than derived from GET /agent so they keep their wording
  // and their order, and so the composer still offers them while that request
  // is in flight. Any *other* primary agent the container declares is merged in
  // by Chat, which is where the list is known.
  { name: "plan", description: "Switch to plan mode — read-only, no edits" },
  { name: "build", description: "Switch to build mode — full tool access" },
  { name: "compact", description: "Summarise the conversation and free context space" },
  { name: "undo", description: "Revert the last user message and its reply" },
  { name: "redo", description: "Restore the most recently reverted messages" },
  { name: "share", description: "Create a shareable link for this session" },
  { name: "unshare", description: "Remove the share link for this session" },
  { name: "export", description: "Download the transcript as Markdown" },
]

// What the agent runs as when nothing has said otherwise. The agent's own
// default (agent/packages/core/src/agent.ts, `defaultID`), repeated because the
// session record does not carry one until a turn has named it.
export const DEFAULT_AGENT = "build"

export interface SlashInput {
  name: string
  // Undefined until whitespace follows the name. That is what separates "still
  // choosing a command" from "choosing its argument", and it is the whole state
  // machine — there is no other flag.
  arg: string | undefined
}

/**
 * The parsed slash command, or undefined if the text is an ordinary message.
 *
 * A slash command has to occupy the *whole* composer, which is the same rule
 * opencode's own composer uses. Two things follow from that and both are the
 * point: "fix this /model" is a message, not a command, and nothing here has to
 * know where the caret is.
 */
export function parseSlash(text: string): SlashInput | undefined {
  if (!text.startsWith("/") || text.includes("\n")) return undefined
  const match = /^\/(\S*)(?:\s+([\s\S]*))?$/.exec(text)
  if (!match) return undefined
  // A bare "/" matches with an empty name, which is the state right after the
  // user types the slash — every command is still a candidate.
  return { name: match[1] ?? "", arg: match[2] }
}

export function findCommand(name: string, commands: SlashCommand[]): SlashCommand | undefined {
  return commands.find((command) => command.name === name)
}

export function matchCommands(name: string, commands: SlashCommand[]): SlashCommand[] {
  return commands.filter((command) => command.name.startsWith(name))
}

// Variants arrive from GET /api/model as { id, headers, body } — there is no
// display name on the wire, so the wording lives here. The product has three
// choices; absent and old "default" selections both mean Medium.
const EFFORT_LABELS: Record<string, { label: string; hint: string }> = {
  // The effort opencode uses for its own small requests, such as the session
  // title. Offered to people too, since the gateway accepts it.
  low: { label: "Low", hint: "Lighter reasoning — faster and cheaper" },
  // The gateway has accepted "medium" since product.ts was written, but the
  // container's config only ever declared "high", so it never reached a picker.
  // Both copies now build their variant list from GET /v1/models, so this row
  // appears wherever the gateway says it should.
  medium: { label: "Medium", hint: "Balanced reasoning — selected initially" },
  high: { label: "High", hint: "Deeper reasoning — slower and more expensive" },
}

export const DEFAULT_EFFORT = "medium"

export function effortId(id: string | undefined): string {
  return !id || id === "default" ? DEFAULT_EFFORT : id
}

export function effortLabel(id: string | undefined): string {
  return EFFORT_LABELS[effortId(id)]?.label ?? effortId(id)
}

export function effortHint(id: string): string | undefined {
  return EFFORT_LABELS[id]?.hint
}

export function modelKey(ref: Pick<ModelRef, "id" | "providerID">): string {
  return `${ref.providerID}/${ref.id}`
}

export function sameModel(a: ModelRef | undefined, b: ModelRef | undefined): boolean {
  if (!a || !b) return a === b
  return a.id === b.id && a.providerID === b.providerID && effortId(a.variant) === effortId(b.variant)
}

/** The models to offer for `/model`, narrowed by whatever was typed after it. */
export function matchModels(models: Model[], query: string | undefined): Model[] {
  const needle = query?.trim().toLowerCase()
  if (!needle) return models
  return models.filter(
    (model) => model.name.toLowerCase().includes(needle) || modelKey(model).toLowerCase().includes(needle),
  )
}

/**
 * The effort levels to offer for `/effort`: the model's default, plus one per
 * variant the model actually declares.
 *
 * Only offering declared variants is the guard against VariantUnavailableError.
 * switchModel does not validate what it is given — a bad variant is accepted
 * and then fails at the *next turn* — so a workspace image whose config
 * predates variants must show nothing to pick rather than something that dies
 * later.
 */
export function effortOptions(model: Model | undefined): string[] {
  const declared = model?.variants?.map((variant) => variant.id) ?? []
  const available = new Set([DEFAULT_EFFORT, ...declared.filter((id) => id !== "default")])
  return ["low", "medium", "high"].filter((id) => available.has(id))
}

/**
 * The agents a person may switch to, narrowed by whatever was typed after
 * `/agent`.
 *
 * Subagents are dropped: `general` and `explore` are spawned by the task tool
 * and are not something a session can run as. This is the same filter the TUI
 * applies in packages/tui/src/context/local.tsx.
 */
export function selectableAgents(agents: AgentInfo[]): AgentInfo[] {
  return agents.filter((agent) => agent.mode !== "subagent" && !agent.hidden)
}

export function matchAgents(agents: AgentInfo[], query: string | undefined): AgentInfo[] {
  const needle = query?.trim().toLowerCase()
  if (!needle) return agents
  return agents.filter(
    (agent) =>
      agent.id.toLowerCase().includes(needle) ||
      (agent.description?.toLowerCase().includes(needle) ?? false),
  )
}
